import React from 'react';
import { AlertCircle, CheckCircle, Clock, XCircle, Info } from 'lucide-react';
import Stepper from './Stepper';
import ProcedureGroups from './ProcedureGroups';
import ProofRounds from './ProofRounds';
import { PRIMARY_BUTTON } from './FormShell';
import {
    statusLabel, statusStyle, trackerSteps, trackerRound, nextAction, historyLabel, showsProcedures, approvedTotalOf,
    proofRounds, formatInr, formatDate,
} from './reimbursementStatus';

const NEXT_TONE = {
    review: { box: 'bg-portal-purple-light border-portal-purple-border', title: 'text-portal-purple-dark', Icon: Clock },
    action: { box: 'bg-portal-orange-light border-portal-orange-border', title: 'text-portal-orange-ink', Icon: AlertCircle },
    success: { box: 'bg-portal-green-light border-portal-green-border', title: 'text-portal-green-ink', Icon: CheckCircle },
    danger: { box: 'bg-red-50 border-red-200', title: 'text-red-700', Icon: XCircle },
};

const ACTION_TEST_ID = { upload_proof: 'upload-proof-of-treatment', reupload: 'next-step-reupload' };

// The one thing to know or do next (nextAction): what is happening, any notes (more approved,
// still under review), what the reviewer asked for, and the single button when there is something
// to do.
const NextActionBlock = ({ next, onAction }) => {
    const tone = NEXT_TONE[next.tone] || NEXT_TONE.review;
    const { Icon } = tone;
    return (
        <div className={`border rounded-xl p-3 mt-4 ${tone.box}`} data-testid="next-action">
            <div className="flex gap-2.5">
                <Icon className={`w-5 h-5 flex-shrink-0 ${tone.title}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold ${tone.title}`} data-testid="next-step-title">{next.title}</p>
                    <p className="text-xs text-portal-ink mt-1 leading-relaxed">{next.body}</p>
                    {next.notes.length > 0 && (
                        <ul className="mt-2 space-y-1">
                            {next.notes.map((note) => (
                                <li key={note} className="flex gap-1.5 text-xs text-portal-ink">
                                    <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px text-portal-sub" aria-hidden="true" /> {note}
                                </li>
                            ))}
                        </ul>
                    )}
                    {next.documents.length > 0 ? (
                        <div className="mt-2 text-xs bg-white/70 border border-portal-orange-border rounded-lg p-2.5" data-testid="reviewer-request">
                            <p className="font-semibold text-portal-ink">Documents to re-upload: {next.documents.join(', ')}</p>
                            {next.reviewerNote && <p className="mt-1 text-portal-ink">Reviewer note: “{next.reviewerNote}”</p>}
                        </div>
                    ) : next.reviewerNote && (
                        <p className="mt-2 text-xs text-portal-ink bg-white/70 border border-portal-border rounded-lg p-2.5">Reviewer note: “{next.reviewerNote}”</p>
                    )}
                </div>
            </div>
            {next.action && (
                <button
                    type="button"
                    onClick={() => onAction(next.action)}
                    data-testid={ACTION_TEST_ID[next.action]}
                    className={`${PRIMARY_BUTTON} w-full mt-3`}
                >
                    {next.actionLabel}
                </button>
            )}
        </div>
    );
};

// The activity log, newest first. Entries about a proof name it ("You uploaded proof #2").
const ActivityLog = ({ reimbursement }) => {
    const history = reimbursement.status_history || [];
    if (!history.length) return null;
    const roundOf = Object.fromEntries(proofRounds(reimbursement).map((round) => [String(round._id), round.round]));
    return (
        <details className="mt-4 group">
            <summary className="text-xs font-semibold text-portal-purple-dark cursor-pointer w-fit">Show activity</summary>
            <ol className="mt-2 space-y-2 border-l-2 border-portal-border pl-3" data-testid="reimbursement-timeline">
                {[...history].reverse().map((entry, i) => (
                    <li key={`${entry.at}-${i}`} className="text-xs">
                        <p className="font-semibold text-portal-ink">{historyLabel(entry.action, { ...entry, round: roundOf[String(entry.claim_id)] })}</p>
                        <p className="text-portal-sub">{formatDate(entry.at)}{entry.role === 'reviewer' ? ' · ToothLens reviewer' : ''}</p>
                        {entry.note && <p className="text-portal-ink mt-0.5">“{entry.note}”</p>}
                    </li>
                ))}
            </ol>
        </details>
    );
};

// One Reimbursement: who it is for, where it stands, the one next step, its procedures, every proof
// submitted, and what has happened. Starting a new Reimbursement is offered once for the whole
// section, so a closed card shows no button of its own.
const ReimbursementCard = ({ reimbursement, patientLabel, onAction }) => {
    const status = reimbursement.reimbursement_status;
    const next = nextAction(reimbursement);
    const approved = approvedTotalOf(reimbursement);
    const title = patientLabel || reimbursement.clinic_name || 'Your reimbursement';
    const subtitle = [
        patientLabel ? reimbursement.clinic_name : null,
        reimbursement.createdAt ? `Requested ${formatDate(reimbursement.createdAt)}` : null,
    ].filter(Boolean).join(' · ');

    return (
        <article className="bg-white border border-portal-border rounded-2xl p-4 mb-4 shadow-[0_1px_2px_rgba(28,34,48,0.04)]" data-testid="reimbursement-current">
            <div className="min-w-0">
                <p className="font-bold text-portal-ink text-[15px] leading-snug">{title}</p>
                {subtitle && <p className="text-xs text-portal-sub mt-0.5">{subtitle}</p>}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 mt-2.5">
                <span data-testid="reimbursement-status" className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusStyle(status)}`}>
                    {statusLabel(status)}
                </span>
                {approved > 0 && (
                    <span className="text-xs text-portal-sub">Approved <strong className="text-sm text-portal-ink">{formatInr(approved)}</strong></span>
                )}
            </div>

            <Stepper steps={trackerSteps(status, trackerRound(reimbursement))} label="Reimbursement progress" className="mt-4" />

            {next && <NextActionBlock next={next} onAction={(action) => onAction(action, reimbursement)} />}

            {showsProcedures(reimbursement) && <ProcedureGroups reimbursement={reimbursement} />}

            <ProofRounds reimbursement={reimbursement} />

            <ActivityLog reimbursement={reimbursement} />
        </article>
    );
};

export default ReimbursementCard;
